import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import { mangaList, categories } from '@/mocks/manga';

const categoryMap: Record<string, string> = {
  romance: '恋爱',
  action: '动作',
  fantasy: '奇幻',
  drama: '剧情',
  thriller: '悬疑',
  comedy: '喜剧',
  horror: '恐怖',
  mature: '都市',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentSlug = slug || 'all';

  const filteredManga = currentSlug === 'all'
    ? mangaList
    : mangaList.filter((m) => m.category === currentSlug);

  const selectedCategory = categories.find((c) => c.slug === currentSlug);
  const categoryName = selectedCategory?.name || categoryMap[currentSlug] || '全部';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName}韩漫 - 韩漫漫画网`,
    description: `韩漫漫画网${categoryName}韩漫分类，收录${filteredManga.length}部${categoryName}类韩国漫画，免费在线阅读，无删减下拉式阅读体验。`,
    url: `https://hanmanmh.com/category/${currentSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredManga.map((m, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Book',
          name: m.title,
          author: { '@type': 'Person', name: m.author },
          url: `https://hanmanmh.com/article/${m.id}`,
        },
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={`${categoryName}韩漫 - 韩漫漫画网 | 分类浏览`}
        description={`韩漫漫画网${categoryName}韩漫分类，收录${filteredManga.length}部${categoryName}类韩国漫画，免费在线阅读，无删减下拉式韩漫大全。`}
        keywords={`${categoryName}韩漫,韩漫漫画网,韩漫在线阅读,免费韩国漫画`}
        canonicalUrl={`https://hanmanmh.com/category/${currentSlug}`}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-50 border-b border-background-200">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${categoryName}韩漫` },
            ]}
          />
          <div className="max-w-7xl mx-auto px-4 md:px-6 pb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
              <strong>{categoryName}韩漫</strong>
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filteredManga.length} 部{categoryName}类韩国漫画，畅享免费在线阅读
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-3 scrollbar-hide flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  currentSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-sm`}></i>
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 pb-16">
          {filteredManga.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类暂无漫画，请查看其他分类</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {filteredManga.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/article/${manga.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
                    <img
                      src={manga.cover}
                      alt={manga.title}
                      title={`${manga.title} - ${categoryName}韩漫 - 韩漫漫画网`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                    <div className="absolute bottom-2 left-2 right-2">
                      <span className="inline-block px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full">
                        {manga.rating}
                      </span>
                    </div>
                    {manga.status === '连载中' && (
                      <div className="absolute top-2 right-2">
                        <span className="inline-block px-2 py-0.5 bg-accent-500 text-white text-xs rounded-full">
                          连载中
                        </span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                    {manga.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{manga.author}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}